"use client";

import { Callsign } from "@modernatc/ui/callsign";
import { cn } from "@modernatc/ui/cn";
import * as React from "react";
import type { MetarData } from "@/lib/metar";
import type { ParagraphSpotlight } from "@/lib/paragraphs";

/**
 * HeroSpotlight — the live-data panel that anchors the landing page.
 *
 * One focused element: a cycling spotlight on real JO 7110.65 paragraphs.
 * Accents:
 *   - Live UTC clock in the header (ticks each second).
 *   - Live METAR for KCHS in the footer, refreshed at the server fetch
 *     layer every 5 minutes.
 *
 * Replaces the previous "moving aircraft" radar mockup. The brand
 * promise is depth-of-source and live currency with the FAA's
 * publications — this panel makes both visible at first glance.
 */

interface HeroSpotlightProps {
  metar: MetarData | null;
  paragraphs: ReadonlyArray<ParagraphSpotlight>;
  /** How often to rotate to the next paragraph, in milliseconds. */
  cycleMs?: number;
}

const DEFAULT_CYCLE_MS = 8_000;
const FADE_MS = 600;

export function HeroSpotlight({
  metar,
  paragraphs,
  cycleMs = DEFAULT_CYCLE_MS,
}: HeroSpotlightProps) {
  const [idx, setIdx] = React.useState(0);
  const [fadingOut, setFadingOut] = React.useState(false);
  const [utc, setUtc] = React.useState(() => formatUtc(new Date()));

  React.useEffect(() => {
    if (paragraphs.length <= 1) return;
    const advanceIn = window.setTimeout(() => setFadingOut(true), cycleMs - FADE_MS);
    const swap = window.setTimeout(() => {
      setIdx((i) => (i + 1) % paragraphs.length);
      setFadingOut(false);
    }, cycleMs);
    return () => {
      window.clearTimeout(advanceIn);
      window.clearTimeout(swap);
    };
    // Re-arm whenever idx changes so the cycle continues indefinitely.
  }, [idx, paragraphs.length, cycleMs]);

  React.useEffect(() => {
    const id = window.setInterval(() => setUtc(formatUtc(new Date())), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const current = paragraphs[idx]!;

  return (
    <div className="relative mx-auto w-full max-w-[540px]">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 rounded-full bg-[radial-gradient(circle_at_center,_rgb(16_197_230_/_0.12),_transparent_60%)]"
      />

      <div className="panel-grain overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)]/70 shadow-[var(--shadow-panel)] backdrop-blur-sm">
        {/* Header strip */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-[var(--color-panel-edge)] px-4 py-2.5 text-[10px]">
          <Callsign className="text-[var(--color-amber)]">
            <span className="inline-block animate-pulse">●</span> JO 7110.65 BB · CHG 2 · LIVE
          </Callsign>
          <div className="flex items-center gap-3 text-[var(--color-ink-dim)]">
            <Stat label="UTC">
              <span className="text-mono text-[var(--color-ink)]">{utc}</span>
              <span className="text-[var(--color-amber)]">Z</span>
            </Stat>
            <Stat label="CHG" className="hidden sm:inline-flex">
              <span className="text-[var(--color-amber-bright)]">3</span>
            </Stat>
          </div>
        </div>

        {/* Spotlight body */}
        <div className="relative px-6 py-10 sm:px-8 sm:py-12">
          {/* Decorative range-grid in the background — purely cosmetic */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgb(173_158_110_/_0.06)_0,_transparent_45%)]"
          />
          <div
            className={cn(
              "relative flex flex-col gap-4 transition-opacity",
              fadingOut ? "opacity-0" : "opacity-100",
            )}
            style={{ transitionDuration: `${FADE_MS}ms` }}
            aria-live="polite"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-mono text-2xl font-semibold tracking-tight text-[var(--color-amber-bright)] sm:text-3xl">
                {current.ref}
              </span>
              <Callsign className="rounded-[var(--radius-sm)] border border-[var(--color-amber)]/30 px-1.5 py-0.5 text-[9px] text-[var(--color-amber)]">
                {current.tag}
              </Callsign>
            </div>
            <h2 className="text-balance text-xl font-semibold leading-snug text-[var(--color-ink)] sm:text-2xl">
              {current.title}
            </h2>
            <p className="text-pretty text-sm leading-relaxed text-[var(--color-ink-dim)] sm:text-base">
              {current.summary}
            </p>
          </div>

          {/* Progress dots */}
          <div
            aria-hidden
            className="relative mt-8 flex items-center justify-center gap-1.5"
          >
            {paragraphs.map((p, i) => (
              <span
                key={p.ref}
                className={cn(
                  "size-1.5 rounded-full transition-colors",
                  i === idx ? "bg-[var(--color-amber)]" : "bg-[var(--color-panel-edge)]",
                )}
              />
            ))}
          </div>
        </div>

        {/* METAR strip */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-[var(--color-panel-edge)] px-4 py-2.5 text-[10px]">
          {metar ? (
            <>
              <Callsign className="text-[var(--color-clear)]">
                ◆ {metar.station} METAR
              </Callsign>
              <div className="flex flex-wrap items-center gap-3 text-[var(--color-ink-dim)]">
                <Stat label="WIND">
                  <span className="text-[var(--color-ink)]">{metar.wind}</span>
                </Stat>
                <Stat label="VIS" className="hidden sm:inline-flex">
                  <span className="text-[var(--color-ink)]">{metar.visibility}</span>
                </Stat>
                {metar.tempC != null ? (
                  <Stat label="TEMP">
                    <span className="text-[var(--color-ink)]">{metar.tempC}°C</span>
                  </Stat>
                ) : null}
                {metar.altimeter ? (
                  <Stat label="ALT" className="hidden sm:inline-flex">
                    <span className="text-[var(--color-ink)]">{metar.altimeter}</span>
                  </Stat>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <Callsign className="text-[var(--color-ink-muted)]">
                ◆ METAR · offline
              </Callsign>
              <span className="text-[10px] text-[var(--color-ink-muted)]">
                aviationweather.gov unreachable — try again shortly
              </span>
            </>
          )}
        </div>
      </div>

      <p className="mt-3 text-center text-callsign text-[9px] uppercase tracking-[0.25em] text-[var(--color-ink-muted)]">
        ▸ {paragraphs.length} of {paragraphs.length} cited paragraphs · {paragraphs.length * 4}+ cards drawn from each
      </p>
    </div>
  );
}

function formatUtc(d: Date): string {
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  const s = String(d.getUTCSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function Stat({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-baseline gap-1.5", className)}>
      <span className="text-callsign text-[9px] text-[var(--color-ink-muted)]">
        {label}
      </span>
      {children}
    </span>
  );
}
